
import React, { useEffect, useMemo, useState } from "react";

import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Checkbox,
    Breadcrumbs,
    Link,
    Button,
    Chip,
    Stack,
    InputAdornment,
} from "@mui/material";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HeaderDashboard from '../../components/HeaderDashboard';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import axios from "axios";
import VisualizarPDF from "./VisualizarPDF";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    TrendingUpRounded,
    ArrowForwardRounded,
    AssessmentRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const DocumentosContabeis = () => {
    const navigate = useNavigate();
    const MotionBox = motion(Box);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [busca, setBusca] = useState("");
    const [tipoFiltro, setTipoFiltro] = useState("Todos");
    const [anoFiltro, setAnoFiltro] = useState(new Date().getFullYear().toString());
    const [loading, setLoading] = useState(false);

    const [selecionados, setSelecionados] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [anosDisponiveis, setAnosDisponiveis] = useState({});

    const [visualizarPDF, setVisualizarPDF] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [documentoSelecionado, setDocumentoSelecionado] = useState(null);

    const getIcon = (tipo) => {

        switch (tipo) {

            case "Balanço":
                return <AccountBalanceOutlinedIcon />;

            case "DRE":
                return <TableChartOutlinedIcon />;

            case "Balancete":
                return <DescriptionOutlinedIcon />;

            default:
                return <DescriptionOutlinedIcon />;
        }
    };

    useEffect(() => {

        const getDocumentos = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/get/demonstracoes`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token")
                    }
                })
                const anos = response.data.demonstracoes.reduce((acc, doc) => {
                    if (!acc.some((item) => item.value === doc.ano.toString())) {
                        acc.push({ ano: `Ano: ${doc.ano}`, value: doc.ano.toString() });
                    }
                    return acc;
                }, []);

                setAnosDisponiveis(anos);

                const documentosFormatados = response.data.demonstracoes.map((documento) => ({
                    ...documento,
                    icon: getIcon(documento.tipo)
                }));

                setDocumentos(documentosFormatados);
            } catch (error) {
                console.error("Erro ao buscar documentos:", error);
            } finally {
                setLoading(false);
            }
        };

        getDocumentos();
    }, []);

    const documentosFiltrados = useMemo(() => {

        return documentos.filter((documento) => {

            const correspondeBusca = documento.nome.toLowerCase().includes(busca.toLowerCase()) || documento.tipo.toLowerCase().includes(busca.toLowerCase());
            const correspondeTipo = tipoFiltro === "Todos" || documento.tipo === tipoFiltro;
            const correspondeAno = anoFiltro === "Todos" || documento.ano === anoFiltro;

            return (correspondeBusca && correspondeTipo && correspondeAno);
        });

    }, [busca, tipoFiltro, anoFiltro, documentos]);


    const todosSelecionados = documentosFiltrados.length > 0 && documentosFiltrados.every((documento) => selecionados.includes(documento.id));

    const selecionarDocumento = (id) => {
        setSelecionados((prev) => {
            if (prev.includes(id)) {
                return prev.filter(
                    (item) => item !== id
                );
            }
            return [...prev, id];
        });
    };

    const selecionarTodos = () => {
        if (todosSelecionados) {
            setSelecionados((prev) =>
                prev.filter(
                    (id) => !documentosFiltrados.some((doc) => doc.id === id)
                )
            );
            return;
        }

        setSelecionados((prev) => [
            ...new Set([
                ...prev,
                ...documentosFiltrados.map(
                    (doc) => doc.id
                ),
            ]),
        ]);
    };

    const limparSelecao = () => {
        setSelecionados([]);
    };

    const fecharPDF = () => {

        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
        }

        setPdfUrl(null);
        setDocumentoSelecionado(null);
        setVisualizarPDF(false);
    };

    const handleVisualizar = async (documento) => {

        try {

            setDocumentoSelecionado(documento);
            setPdfUrl(null);
            setVisualizarPDF(true);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/demonstracoes/${documento.id}/visualizar`,
                {
                    headers: {
                        Authorization: localStorage.getItem("token")
                    },
                    responseType: "blob"
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: "application/pdf"
                })
            );

            setPdfUrl(url);

        } catch (error) {

            console.error(
                "Erro ao visualizar PDF:",
                error
            );

            setVisualizarPDF(false);

        }
    };

    const handleDownload = async (documento) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/demonstracoes/${documento.id}/download`, {
                headers: {
                    Authorization: localStorage.getItem("token")
                },
                responseType: "blob"
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `${documento.tipo}_${documento.competencia}.pdf`);

            document.body.appendChild(link);

            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Erro ao baixar demonstração:", error);
        }
    };

    const handleDownloadSelecionados = async () => {
        try {
            const ids = selecionados;

            if (!ids.length) {
                return;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/demonstracoes/download-zip`, { ids }, {
                headers: {
                    Authorization: localStorage.getItem("token")
                },
                responseType: "blob"
            });

            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));

            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", "demonstracoes-contabeis.zip");

            document.body.appendChild(link);

            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Erro ao baixar demonstrações:", error);
        } finally {
            setSelecionados([]);
        }
    };

    const documentosContabeisInfo = [
        {
            titulo: "Balanço Patrimonial",
            descricao:
                "Mostra a situação financeira e patrimonial da empresa em uma determinada data.",
            explicacao:
                "Ele apresenta o que a empresa possui (ativos), o que deve (passivos) e o valor que pertence aos sócios (patrimônio líquido).",
            icon: <AccountBalanceOutlinedIcon />,
            cor: "#9C01B9"
        },
        {
            titulo: "Balancete",
            descricao:
                "É um retrato dos saldos contábeis da empresa em determinado período.",
            explicacao:
                "Permite acompanhar as contas da empresa ao longo do período e identificar como estão distribuídos os valores registrados na contabilidade.",
            icon: <DescriptionOutlinedIcon />,
            cor: "#1E88E5"
        },
        {
            titulo: "DRE",
            descricao:
                "Mostra se a empresa teve lucro ou prejuízo em determinado período.",
            explicacao:
                "A DRE apresenta receitas, custos e despesas, permitindo entender quanto a empresa faturou, quanto gastou e qual foi o resultado final.",
            icon: <TableChartOutlinedIcon />,
            cor: "#16A05D"
        }
    ];


    const handleAbrirAnalise = () => {
        navigate("/dashboard/analise-contabil");
    };


    return (


        <>
            <HeaderDashboard />
            <div style={{ padding: "30px", background: "#f4f6f8", minHeight: "100vh", marginTop: "64px" }}>
                <div style={{ width: "100%", height: "fit-content", padding: "10px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "20px", marginBottom: "20px" }}>
                        <div style={{ width: "80%" }}>
                            <Breadcrumbs aria-label="breadcrumb">
                                <Link
                                    underline="hover"
                                    sx={{ display: 'flex', alignItems: 'center', cursor: "pointer" }}
                                    color="inherit"
                                    onClick={() => navigate("../dashboard")}
                                >
                                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                    Dashboard
                                </Link>
                                <Typography
                                    sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}
                                >
                                    <ReceiptIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                    Documentos Contábeis
                                </Typography>
                            </Breadcrumbs>
                        </div>
                    </div>
                </div>

                <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <Box sx={{ width: { xs: "100%", md: "80%" }, borderRadius: "8px" }}>
                        {/* <Typography
                            variant="h4"
                            sx={{ fontWeight: 700 }}
                        >
                            Documentos da Empresa
                        </Typography>
                        <Typography
                            sx={{
                                color: "#777",
                                fontSize: {
                                    xs: 13,
                                    md: 14,
                                },
                                lineHeight: 1.6,
                                mb: 3
                            }}
                        >
                            Encontre, visualize e baixe seus documentos contábeis quando precisar para bancos, licitações ou auditorias.
                        </Typography> */}
                        <Box
                            sx={{
                                width: "100%",
                            }}
                        >

                            {/* TÍTULO */}

                            <Box
                                sx={{
                                    mb: 3
                                }}
                            >

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1
                                    }}
                                >

                                    <InfoOutlinedIcon
                                        sx={{
                                            color: "#9C01B9",
                                            fontSize: 25
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: "20px",
                                                md: "24px"
                                            },

                                            fontWeight: 700,
                                            color: "#181818"
                                        }}
                                    >
                                        Entenda seus documentos contábeis
                                    </Typography>

                                </Box>

                                <Typography
                                    sx={{
                                        mt: 1,
                                        fontSize: {
                                            xs: "14px",
                                            md: "15px"
                                        },

                                        lineHeight: 1.6,
                                        color: "#707070",
                                        maxWidth: "780px"
                                    }}
                                >
                                    Esses documentos ajudam a entender a situação financeira
                                    da sua empresa. Cada um apresenta informações diferentes
                                    e, juntos, permitem uma visão mais completa do negócio.
                                </Typography>

                            </Box>


                            {/* CARDS */}

                            <Box
                                sx={{
                                    display: "grid",

                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(3, 1fr)"
                                    },

                                    gap: 2
                                }}
                            >

                                {documentosContabeisInfo.map((documento) => (

                                    <Box
                                        key={documento.titulo}
                                        sx={{
                                            border: "1px solid #E9E9E9",
                                            borderRadius: "16px",
                                            p: {
                                                xs: 2,
                                                md: 2.5
                                            },

                                            backgroundColor: "#FFFFFF",

                                            transition: "all .2s ease",

                                            "&:hover": {
                                                borderColor: `${documento.cor}55`,
                                                transform: "translateY(-2px)",
                                                boxShadow:
                                                    "0 8px 25px rgba(0,0,0,0.06)"
                                            }
                                        }}
                                    >

                                        {/* ÍCONE */}

                                        <Box
                                            sx={{
                                                width: 35,
                                                height: 35,
                                                borderRadius: "12px",

                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",

                                                backgroundColor: `${documento.cor}12`,
                                                color: documento.cor,

                                                mb: 2
                                            }}
                                        >
                                            {documento.icon}
                                        </Box>


                                        {/* TÍTULO */}

                                        <Typography
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: 700,
                                                color: "#202020",
                                                mb: 1
                                            }}
                                        >
                                            {documento.titulo}
                                        </Typography>


                                        {/* DESCRIÇÃO */}

                                        {/* <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                lineHeight: 1.5,
                                                color: "#414141",
                                                mb: 1
                                            }}
                                        >
                                            {documento.descricao}
                                        </Typography> */}


                                        {/* EXPLICAÇÃO */}

                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                lineHeight: 1.6,
                                                color: "#777777"
                                            }}
                                        >
                                            {documento.explicacao}
                                        </Typography>

                                    </Box>

                                ))}

                            </Box>


                            {/* RESUMO */}

                            <Box
                                sx={{
                                    mt: 2,

                                    p: 2,

                                    borderRadius: "12px",

                                    background:
                                        "linear-gradient(135deg, #FBF5FC 0%, #F8FAF9 100%)",

                                    border: "1px solid #EEE2F0"
                                }}
                            >

                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        lineHeight: 1.6,
                                        color: "#555555"
                                    }}
                                >
                                    <strong>Em resumo:</strong>{" "}
                                    o Balanço mostra o que a empresa possui e deve,
                                    o Balancete ajuda a acompanhar os saldos das contas
                                    e a DRE mostra se a empresa teve lucro ou prejuízo.
                                    Esses documentos são importantes para acompanhar a
                                    saúde financeira e tomar decisões melhores para o negócio.
                                </Typography>

                            </Box>

                        </Box>
                    </Box>
                </Box>
                <MotionBox
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    sx={{
                        width: window.innerWidth < 800 ? "100%" : "80%",
                        margin: "20px auto",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: "18px",
                        border: "1px solid rgba(156, 1, 185, 0.14)",
                        background:
                            "linear-gradient(135deg, #ffffff 0%, #faf7fb 100%)",
                        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.06)",
                        p: {
                            xs: 2,
                            sm: 2.5,
                            md: 3,
                        },

                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: "180px",
                            height: "180px",
                            borderRadius: "50%",
                            background: "rgba(156, 1, 185, 0.06)",
                            right: "-70px",
                            top: "-90px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Cabeçalho */}
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={2}
                        sx={{
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    minWidth: 48,
                                    borderRadius: "13px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background:
                                        "linear-gradient(135deg, #9C01B9 0%, #720086 100%)",
                                    color: "#fff",
                                    boxShadow:
                                        "0 6px 16px rgba(156, 1, 185, 0.22)",
                                }}
                            >
                                <AssessmentRounded />
                            </Box>

                            <Box>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    flexWrap="wrap"
                                >
                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: "16px",
                                                sm: "18px",
                                            },
                                            fontWeight: 700,
                                            color: "#1d1d1d",
                                        }}
                                    >
                                        Análise das Demonstrações Contábeis
                                    </Typography>

                                    <Chip
                                        label="NOVO"
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: "10px",
                                            fontWeight: 800,
                                            color: "#fff",
                                            backgroundColor: "#9C01B9",
                                            "& .MuiChip-label": {
                                                px: 1,
                                            },
                                        }}
                                    />
                                </Stack>

                                <Typography
                                    sx={{
                                        mt: 0.4,
                                        fontSize: "13px",
                                        color: "#737373",
                                    }}
                                >
                                    Acompanhe a evolução financeira da sua empresa.
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>

                    {/* Conteúdo */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            mt: 2.5,
                        }}
                    >
                        {/* Rodapé */}
                        <Box
                            sx={{
                                mt: 2.5,
                                display: "flex",
                                alignItems: {
                                    xs: "stretch",
                                    sm: "center",
                                },
                                justifyContent: "space-between",
                                gap: 2,
                                flexDirection: {
                                    xs: "column",
                                    sm: "row",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "#858585",
                                    maxWidth: "520px",
                                }}
                            >
                                Consulte indicadores, evolução financeira e pontos
                                de atenção identificados a partir das suas
                                demonstrações contábeis.
                            </Typography>

                            <Button
                                onClick={handleAbrirAnalise}
                                variant="contained"
                                endIcon={<ArrowForwardRounded />}
                                sx={{
                                    flexShrink: 0,
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    px: 2,
                                    py: 1.1,
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    background:
                                        "linear-gradient(135deg, #9C01B9 0%, #720086 100%)",
                                    boxShadow:
                                        "0 5px 15px rgba(156, 1, 185, 0.20)",
                                    "&:hover": {
                                        background:
                                            "linear-gradient(135deg, #8A00A5 0%, #650076 100%)",
                                        boxShadow:
                                            "0 7px 18px rgba(156, 1, 185, 0.25)",
                                    },
                                }}
                            >
                                Ver análise completa
                            </Button>
                        </Box>
                    </Box>
                </MotionBox>
                <Box sx={{ width: "100%", minHeight: "100%", }} >
                    <Box sx={{ width: window.innerWidth < 960 ? "100%" : "80%", mx: "auto", }} >
                        <Box
                            sx={{
                                background: "#fff",
                                border: "1px solid #E7EBF2",
                                borderRadius: "14px",
                                p: {
                                    xs: 1.2,
                                    md: 1.5,
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: {
                                    xs: "wrap",
                                    md: "nowrap",
                                },
                                boxShadow:
                                    "0 2px 8px rgba(25, 35, 50, 0.03)",
                            }}
                        >

                            <TextField
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar documento..."
                                size="small"
                                sx={{
                                    flex: 1,
                                    minWidth: {
                                        xs: "100%",
                                        md: 260,
                                    },

                                    "& .MuiOutlinedInput-root": {
                                        height: 40,
                                        borderRadius: "9px",
                                        background: "#F8FAFC",
                                        fontSize: 13,

                                        "& fieldset": {
                                            borderColor: "#E5E9EF",
                                        },

                                        "&:hover fieldset": {
                                            borderColor: "#D9DEE7",
                                        },

                                        "&.Mui-focused fieldset": {
                                            borderColor: "#9C01B9",
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchOutlinedIcon sx={{ fontSize: 17, color: "#8A94A6", }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 4px)", md: 125, }, }}  >
                                <Select
                                    value={tipoFiltro}
                                    onChange={(e) =>
                                        setTipoFiltro(e.target.value)
                                    }
                                    IconComponent={
                                        KeyboardArrowDownOutlinedIcon
                                    }
                                    sx={{
                                        height: 40,
                                        borderRadius: "9px",
                                        background: "#F8FAFC",
                                        fontSize: 13,

                                        "& .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                "#E5E9EF",
                                        },
                                    }}
                                >
                                    <MenuItem value="Todos">
                                        Tipo: Todos
                                    </MenuItem>

                                    <MenuItem value="Balanço">
                                        Balanço
                                    </MenuItem>

                                    <MenuItem value="DRE">
                                        DRE
                                    </MenuItem>

                                    <MenuItem value="Balancete">
                                        Balancete
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: {
                                        xs: "calc(50% - 4px)",
                                        md: 105,
                                    },
                                }}
                            >
                                <Select
                                    value={anoFiltro}
                                    onChange={(e) =>
                                        setAnoFiltro(e.target.value)
                                    }
                                    IconComponent={
                                        KeyboardArrowDownOutlinedIcon
                                    }
                                    sx={{
                                        height: 40,
                                        borderRadius: "9px",
                                        background: "#F8FAFC",
                                        fontSize: 13,

                                        "& .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                "#E5E9EF",
                                        },
                                    }}
                                >
                                    {anosDisponiveis?.length > 0 && anosDisponiveis.map((ano) => (
                                        <MenuItem key={ano.value} value={ano.value}>
                                            {ano.ano}
                                        </MenuItem>
                                    ))}

                                    <MenuItem value="Todos">
                                        Todos
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent:
                                    "space-between",
                                alignItems: "center",
                                px: 0.5,
                                py: 1.2,
                                gap: 2,
                            }}
                        >

                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    color: "#64748B",
                                }}
                            >
                                Exibindo{" "}
                                <strong>
                                    {documentosFiltrados.length}
                                </strong>{" "}
                                documentos contábeis
                                <Box
                                    component="span"
                                    sx={{
                                        mx: 1,
                                        color: "#CBD5E1",
                                    }}
                                >
                                    •
                                </Box>
                                Todos com validade jurídica
                            </Typography>

                            <Button
                                onClick={selecionarTodos}
                                variant="text"
                                sx={{
                                    minWidth: "auto",
                                    p: 0,
                                    color: "#B000FF",
                                    fontSize: 10.5,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {todosSelecionados
                                    ? "Desmarcar todos"
                                    : "Selecionar todos"}
                            </Button>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >

                            {documentosFiltrados.map(
                                (documento) => {

                                    const selecionado =
                                        selecionados.includes(
                                            documento.id
                                        );

                                    return (
                                        <Box
                                            key={documento.id}
                                            sx={{
                                                background:
                                                    "#FFFFFF",
                                                border:
                                                    selecionado
                                                        ? "1px solid #B000FF"
                                                        : "1px solid #E6EAF0",
                                                borderRadius:
                                                    "14px",
                                                minHeight: {
                                                    xs: "auto",
                                                    md: 74,
                                                },
                                                px: {
                                                    xs: 1.2,
                                                    md: 1.5,
                                                },
                                                py: {
                                                    xs: 1.5,
                                                    md: 1.2,
                                                },
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                gap: {
                                                    xs: 1,
                                                    md: 1.5,
                                                },
                                                transition:
                                                    "all 0.18s ease",

                                                boxShadow:
                                                    selecionado
                                                        ? "0 3px 12px rgba(156,1,185,0.08)"
                                                        : "0 2px 6px rgba(25,35,50,0.025)",

                                                "&:hover": {
                                                    borderColor:
                                                        "#D9B0E4",
                                                },
                                            }}
                                        >

                                            <Checkbox
                                                checked={
                                                    selecionado
                                                }
                                                onChange={() =>
                                                    selecionarDocumento(
                                                        documento.id
                                                    )
                                                }
                                                sx={{
                                                    p: 0.5,

                                                    color:
                                                        "#657184",

                                                    "&.Mui-checked":
                                                    {
                                                        color:
                                                            "#9C01B9",
                                                    },
                                                }}
                                            />

                                            {/* ÍCONE */}

                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    minWidth: 34,
                                                    borderRadius:
                                                        "9px",
                                                    background:
                                                        "#FBF1FF",
                                                    border:
                                                        "1px solid #EED4F7",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    color:
                                                        "#B000FF",
                                                }}
                                            >
                                                {documento.icon}
                                            </Box>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >

                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        gap: 0.7,
                                                        flexWrap:
                                                            "wrap",
                                                    }}
                                                >

                                                    <Typography
                                                        sx={{
                                                            fontSize: {
                                                                xs: 12,
                                                                md: 13,
                                                            },
                                                            fontWeight: 700,
                                                            color:
                                                                "#233344",
                                                            lineHeight:
                                                                1.2,
                                                        }}
                                                    >
                                                        {
                                                            documento.nome
                                                        }
                                                    </Typography>

                                                    <Chip
                                                        label="Disponível"
                                                        size="small"
                                                        sx={{
                                                            height: 19,
                                                            borderRadius:
                                                                "10px",
                                                            background:
                                                                "#ECFFF5",
                                                            border:
                                                                "1px solid #B7F2D0",
                                                            color:
                                                                "#10A957",
                                                            fontSize: 11,
                                                            fontWeight: 600,

                                                            "& .MuiChip-label":
                                                            {
                                                                px:
                                                                    0.8,
                                                            },

                                                            "&:before":
                                                            {
                                                                content:
                                                                    '""',
                                                                width: 4,
                                                                height: 4,
                                                                borderRadius:
                                                                    "50%",
                                                                background:
                                                                    "#16C76A",
                                                                ml: 0.5,
                                                            },
                                                        }}
                                                    />

                                                    <Chip
                                                        label={`PDF • ${documento.tamanho}`}
                                                        size="small"
                                                        sx={{
                                                            display: {
                                                                xs: "none",
                                                                sm: "flex",
                                                            },
                                                            height: 18,
                                                            borderRadius:
                                                                "6px",
                                                            background:
                                                                "#F4F6F8",
                                                            color:
                                                                "#8993A3",
                                                            fontSize: 9,
                                                            fontWeight: 500,

                                                            "& .MuiChip-label":
                                                            {
                                                                px:
                                                                    0.8,
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        gap: 0.6,
                                                        flexWrap:
                                                            "wrap",
                                                        mt: 0.4,
                                                    }}
                                                >

                                                    <CalendarMonthOutlinedIcon
                                                        sx={{
                                                            fontSize: 12,
                                                            color:
                                                                "#8C97A8",
                                                        }}
                                                    />

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11,
                                                            color:
                                                                "#657184",
                                                        }}
                                                    >
                                                        Competência:{" "}
                                                        {
                                                            documento.competencia
                                                        }
                                                    </Typography>

                                                    <Box
                                                        sx={{
                                                            color:
                                                                "#CBD1D9",
                                                            fontSize: 9,
                                                        }}
                                                    >
                                                        •
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 10,
                                                            color:
                                                                "#7D8797",
                                                        }}
                                                    >
                                                        {
                                                            documento.descricao
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 0.7,
                                                    flexShrink: 0,
                                                }}
                                            >

                                                <Button
                                                    onClick={() =>
                                                        handleVisualizar(
                                                            documento
                                                        )
                                                    }
                                                    variant="outlined"
                                                    startIcon={
                                                        <VisibilityOutlinedIcon />
                                                    }
                                                    sx={{
                                                        height: 36,
                                                        px: {
                                                            xs: 1,
                                                            md: 1.5,
                                                        },
                                                        minWidth: {
                                                            xs: 36,
                                                            md: 82,
                                                        },
                                                        borderRadius:
                                                            "9px",
                                                        borderColor:
                                                            "#E3E8EF",
                                                        color:
                                                            "#233344",
                                                        fontSize: 10.5,
                                                        textTransform:
                                                            "none",

                                                        "& .MuiButton-startIcon":
                                                        {
                                                            mr: {
                                                                xs: 0,
                                                                md: 0.5,
                                                            },
                                                        },

                                                        "& .MuiSvgIcon-root":
                                                        {
                                                            fontSize: 14,
                                                        },

                                                        "&:hover": {
                                                            borderColor:
                                                                "#C9D0DA",
                                                            background:
                                                                "#F8FAFC",
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: {
                                                                xs: "none",
                                                                md: "block",
                                                            },
                                                        }}
                                                    >
                                                        Visualizar
                                                    </Box>
                                                </Button>

                                                <Button
                                                    onClick={() =>
                                                        handleDownload(
                                                            documento
                                                        )
                                                    }
                                                    variant="contained"
                                                    startIcon={
                                                        <DownloadOutlinedIcon />
                                                    }
                                                    sx={{
                                                        height: 36,
                                                        px: {
                                                            xs: 1,
                                                            md: 1.5,
                                                        },
                                                        minWidth: {
                                                            xs: 36,
                                                            md: 68,
                                                        },
                                                        borderRadius:
                                                            "9px",
                                                        background:
                                                            "#111827",
                                                        boxShadow:
                                                            "none",
                                                        fontSize: 10.5,
                                                        textTransform:
                                                            "none",

                                                        "& .MuiButton-startIcon":
                                                        {
                                                            mr: {
                                                                xs: 0,
                                                                md: 0.5,
                                                            },
                                                        },

                                                        "& .MuiSvgIcon-root":
                                                        {
                                                            fontSize: 14,
                                                        },

                                                        "&:hover": {
                                                            background:
                                                                "#1F2937",
                                                            boxShadow:
                                                                "none",
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: {
                                                                xs: "none",
                                                                md: "block",
                                                            },
                                                        }}
                                                    >
                                                        Baixar
                                                    </Box>
                                                </Button>

                                            </Box>
                                        </Box>
                                    );
                                }
                            )}

                            {documentosFiltrados.length === 0 && (
                                <Box
                                    sx={{
                                        py: 8,
                                        textAlign: "center",
                                        background: "#fff",
                                        border:
                                            "1px solid #E6EAF0",
                                        borderRadius: "14px",
                                    }}
                                >
                                    <DescriptionOutlinedIcon
                                        sx={{
                                            fontSize: 38,
                                            color: "#CBD5E1",
                                            mb: 1,
                                        }}
                                    />

                                    <Typography
                                        fontWeight={700}
                                        color="#233344"
                                    >
                                        Nenhum documento encontrado
                                    </Typography>

                                    <Typography
                                        fontSize={13}
                                        color="#7A8595"
                                        mt={0.5}
                                    >
                                        Tente alterar os filtros ou
                                        realizar uma nova busca.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box
                            sx={{
                                mt: 2.5,
                                pt: 1.5,
                                borderTop:
                                    "1px solid #E8ECF1",
                                display: "flex",
                                justifyContent:
                                    "space-between",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    gap: 0.7,
                                }}
                            >
                                <LockOutlinedIcon
                                    sx={{
                                        fontSize: 13,
                                        color: "#16B866",
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize: 9.5,
                                        color: "#718096",
                                    }}
                                >
                                    Todos os arquivos são
                                    assinados com certificado
                                    ICP-Brasil e possuem valor
                                    legal.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 9.5,
                                        color: "#718096",
                                        cursor: "pointer",
                                        "&:hover": {
                                            color: "#9C01B9",
                                        },
                                    }}
                                >
                                    Manual de Validação
                                </Typography>

                                <Typography
                                    color="#CBD5E1"
                                    fontSize={10}
                                >
                                    •
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 9.5,
                                        color: "#718096",
                                        cursor: "pointer",
                                        "&:hover": {
                                            color: "#9C01B9",
                                        },
                                    }}
                                >
                                    Histórico de Competências
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {selecionados.length > 0 && (
                        <Box
                            sx={{
                                position: "fixed",
                                left: {
                                    xs: 12,
                                    md: "50%",
                                },
                                right: {
                                    xs: 12,
                                    md: "auto",
                                },
                                bottom: {
                                    xs: 12,
                                    md: 24,
                                },
                                transform: {
                                    xs: "none",
                                    md: "translateX(-50%)",
                                },
                                width: {
                                    xs: "auto",
                                    md: "490px",
                                },
                                maxWidth: "calc(100vw - 24px)",
                                background: "#fff",
                                border:
                                    "1px solid #E5E8EE",
                                borderRadius: "16px",
                                boxShadow:
                                    "0 12px 35px rgba(20, 25, 40, 0.15)",
                                p: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                zIndex: 1300,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    minWidth: 30,
                                    borderRadius: "9px",
                                    background:
                                        "#FBF0FF",
                                    border:
                                        "1px solid #EBCDF7",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "center",
                                    color: "#9C01B9",
                                    fontWeight: 700,
                                    fontSize: 12,
                                }}
                            >
                                {selecionados.length}
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#233344",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {selecionados.length}{" "}
                                    {selecionados.length === 1
                                        ? "documento"
                                        : "documentos"}{" "}
                                    selecionado
                                    {selecionados.length !== 1
                                        ? "s"
                                        : ""}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: "#8791A1",
                                        mt: 0.2,
                                    }}
                                >
                                    Será gerado um arquivo ZIP
                                    compactado
                                </Typography>
                            </Box>
                            <Button
                                onClick={limparSelecao}
                                sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    color: "#8791A1",
                                    fontSize: 9.5,
                                    textTransform:
                                        "none",

                                    "&:hover": {
                                        background:
                                            "#F6F7F9",
                                    },
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={
                                    handleDownloadSelecionados
                                }
                                variant="contained"
                                startIcon={
                                    <FolderZipOutlinedIcon />
                                }
                                sx={{
                                    height: 38,
                                    px: 1.5,
                                    borderRadius: "9px",
                                    background:
                                        "linear-gradient(135deg, #9C01B9 0%, #B800D8 100%)",
                                    boxShadow:
                                        "0 5px 14px rgba(156,1,185,0.22)",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    textTransform:
                                        "none",
                                    whiteSpace:
                                        "nowrap",

                                    "& .MuiButton-startIcon":
                                    {
                                        mr: 0.5,
                                    },

                                    "& .MuiSvgIcon-root":
                                    {
                                        fontSize: 15,
                                    },

                                    "&:hover": {
                                        background:
                                            "linear-gradient(135deg, #8C00A8 0%, #A500C3 100%)",
                                        boxShadow:
                                            "0 6px 16px rgba(156,1,185,0.28)",
                                    },
                                }}
                            >
                                Baixar selecionados
                            </Button>
                        </Box>
                    )}
                </Box>
            </div>
            <Loading open={loading} />
            <VisualizarPDF
                open={visualizarPDF}
                onClose={fecharPDF}
                pdfUrl={pdfUrl}
                nomeArquivo={documentoSelecionado?.tipo}
            />
        </>
    );
};

export default DocumentosContabeis;